import React, { useState, useMemo } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import SerialNumberInput from 'components/HGForm/SerialNumberInput';
import { useJwtToken } from 'helper';
import { updateDevice } from 'components/DeviceDetails/service';
import HGFormSubmitBtn from 'components/HGForm/HGFormSubmitBtn';
import HGFormInput from 'components/HGForm/HGFormInput';
import { Label } from 'components/shadcn/ui/label';
import { useQueryClient } from 'react-query';

const MicroinverterForm = ({ register, device }) => {
    const accessToken = useJwtToken();
    const [errorMessage, setErrorMessage] = useState(null);
    const queryClient = useQueryClient();

    // For when user goes back to correct devices serial numbers
    const devicesFromStorage = JSON.parse(sessionStorage.getItem('devices'));
    const [totalFields, setTotalFields] = useState(
        Array.from({
            length: devicesFromStorage
                ? Object.keys(devicesFromStorage).length
                : 1,
        })
    );

    const initialValues = useMemo(
        () =>
            totalFields.reduce((acc, _, index) => {
                const key = `microinverter${index + 1}`;
                acc[key] = devicesFromStorage ? devicesFromStorage[key] : '';
                return acc;
            }, {}),
        [totalFields, devicesFromStorage]
    );

    const schemaShape = useMemo(
        () =>
            totalFields.reduce((acc, _, index) => {
                const key = `microinverter${index + 1}`;
                acc[key] = Yup.string().required(
                    `Serial No. ${index + 1} is required`
                );
                return acc;
            }, {}),
        [totalFields]
    );
    const validationSchema = Yup.object().shape(schemaShape);

    const changeNumberOfFields = (inputValue, formValues) => {
        // Fix: When typing, totalFields value is a string and if user backspace till empty, all serial No. will be deleted from sessionStorage when entering a new value.  If user enters a number greater than 1, all fields will display , but only field with number corresponding to entered value will be placed in sessionStorage.  Thus input error of uncontrolled to controlled will be thrown.
        if (inputValue > 256) return;
        if (inputValue < totalFields.length) {
            delete formValues[`microinverter${totalFields.length}`];
        } else {
            formValues[`microinverter${inputValue}`] = '';
        }
        sessionStorage.setItem('devices', JSON.stringify(formValues));
        setTotalFields(Array.from({ length: inputValue }));
    };

    const handleAddMicros = async (values) => {
        setErrorMessage(null);
        sessionStorage.setItem('devices', JSON.stringify(values));
        const newMicroinverters = Object.entries(values).map(
            ([key, value]) => ({
                serialNumber: value,
            })
        );

        try {
            const deviceWithNewMircos = {
                ...device,
                Microinverters: newMicroinverters,
            };
            const updatedDevice = await updateDevice(
                accessToken,
                deviceWithNewMircos
            );

            if (
                !updatedDevice.Microinverters ||
                updatedDevice.Microinverters.length === 0
            ) {
                throw new Error(
                    'No new microinverters found after device updated.'
                );
            }

            const deviceWithStatuses = updatedDevice.Microinverters.map(
                (device) => ({
                    ...device,
                    status: 'offline',
                })
            );

            queryClient.invalidateQueries(['device', device.Thing_ID]);

            register(deviceWithStatuses);
        } catch (error) {
            console.error(
                'Error updating device with new microinverters: ',
                error
            );
            setErrorMessage('Error to register microinverters: ');
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleAddMicros}
            enableReinitialize // prevents Error: component changing an uncontrolled input to be controlled
        >
            {({ values }) => (
                <Form>
                    <NumberOfMicroinvertersInput
                        totalFields={totalFields}
                        handleChange={(e) =>
                            changeNumberOfFields(e.target.value, values)
                        }
                    />
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                        {totalFields.map((_, index) => (
                            <SerialNumberInput
                                key={index}
                                name={`microinverter${index + 1}`}
                                placeholder={`Enter serial no. for microInverter ${index + 1}`}
                                label={`Microinverter ${index + 1}`}
                            />
                        ))}
                    </div>
                    {errorMessage && (
                        <div className="mt-3 bg-error text-error-foreground">
                            {errorMessage}
                        </div>
                    )}
                    <HGFormSubmitBtn
                        btnText="Register"
                        loading={false}
                        disabled={
                            totalFields.length === 0 ||
                            totalFields.some(
                                (_, index) =>
                                    !values[`microinverter${index + 1}`] ||
                                    !values[`microinverter${index + 1}`].trim()
                                        .length ||
                                    values[`microinverter${index + 1}`].trim()
                                        .length < 5
                            )
                        }
                    />
                </Form>
            )}
        </Formik>
    );
};

const NumberOfMicroinvertersInput = ({ totalFields, handleChange }) => {
    return (
        <div className="mb-3">
            <Label htmlFor="numberOfMicroinverters" className="mb-3">
                Number of microinverters to be installed
            </Label>
            <HGFormInput
                name="numberOfMicroinverters"
                type="number"
                placeholder="Enter number of microinverters"
                onChange={handleChange}
                value={totalFields.length || ''}
                max={256}
                className="mt-2 w-[70px] md:mt-0 md:w-[100px] lg:w-[150px] xl:w-[200px] 2xl:w-[250px]"
            />
        </div>
    );
};
export default MicroinverterForm;
